/**
 * Types + helpers for the DIFOT report.
 *
 * Data flows: React → Worker (/api/reports/difot*) → Supabase. All aggregation
 * happens in Postgres (difot_flow_report() / v_difot_stops); the browser only shapes
 * and renders.
 */
import { apiFetch } from './api';
import { businessTodayISO, businessYesterdayISO } from './dates';

export interface DifotTotals {
  total: number;
  completed: number;
  failed: number;
  pending: number;
  cancelled: number;
  /** Anything still moving: Arrived, Enroute, On Hold, Delayed. */
  in_progress: number;
  /** Attempted — every stop that wasn't cancelled. The DIFOT denominator. */
  denom: number;
  /** Strict measure: Complete AND in full AND completed on the planned day. Not what the KPI card shows. */
  difot: number;
  /** Completions that had an SMS delivery window on file — the On Time denominator. */
  completed_with_window: number;
  /** Completions that landed inside that window — the On Time numerator. */
  in_window_n: number;
  /** Completions that finished on their planned trip_date (the DIFOT "on time" leg). */
  on_time_n: number;
  drivers: number;
  avg_service: number | null;
}

/**
 * Pieces collected, parsed from the drivers' proof-of-delivery answers. Collect
 * runs record bins and bags; Primary network runs record bins/cages, hook bins
 * and bulka bags. A measure a flow doesn't record is 0.
 */
export interface CollectedCounts {
  bins: number;
  bags: number;
  cages: number;
  hook_bins: number;
  bulka_bags: number;
}

export interface DifotCollected extends CollectedCounts {
  /** count × bin size, only where the driver gave a size ("2 x 240"). */
  bin_litres: number;
  completed_stops: number;
  /** Completed stops whose driver recorded any count. */
  counted_stops: number;
  /** Stops whose bin answer carried a size, i.e. that feed bin_litres. */
  sized_stops: number;
}

export interface DifotDaily extends CollectedCounts {
  d: string; // YYYY-MM-DD
  total: number;
  completed: number;
  failed: number;
  pending: number;
  denom: number;
  difot: number;
  with_window: number;
  in_window_n: number;
}

export interface DifotRegionRow extends CollectedCounts {
  /** team_region_id; null for stops booked under no region. */
  id: number | null;
  region: string;
  /** Parent region's name, e.g. "Cleanaway" for "Pilbara". */
  parent: string | null;
  total: number;
  complete: number;
  failed: number;
  pending: number;
  denom: number;
  difot: number;
  with_window: number;
  in_window_n: number;
}

export interface DifotDriverRow {
  driver: string;
  /** The region most of this driver's stops in the window were booked under. */
  region: string | null;
  total: number;
  completed: number;
  failed: number;
  denom: number;
  with_window: number;
  in_window_n: number;
  on_time_n: number;
  difot: number;
  avg_service: number | null;
}

/**
 * WARRRL's two operations, told apart by the root team region a stop is booked
 * under: Containers for Change Collect and the Primary network (Cleanaway).
 */
export type Flow = 'collect' | 'primary';

export const FLOWS: { key: Flow; label: string; blurb: string }[] = [
  { key: 'collect', label: 'Containers for Change Collect', blurb: 'Refund-point collection runs' },
  { key: 'primary', label: 'Primary network', blurb: 'Cleanaway regional network' },
];

/** The collected measures each flow's drivers record, in display order. */
export const COLLECTED_MEASURES: Record<Flow, { key: keyof CollectedCounts; label: string; color: string }[]> = {
  collect: [
    { key: 'bins', label: 'Bins', color: '#13a874' },
    { key: 'bags', label: 'Bags', color: '#6abd5f' },
  ],
  primary: [
    { key: 'cages', label: 'Bins / cages', color: '#0a5c40' },
    { key: 'hook_bins', label: 'Hook bins', color: '#13a874' },
    { key: 'bulka_bags', label: 'Bulka bags', color: '#6abd5f' },
  ],
};

/** A team region offered in the Region filter, already ordered parent-first. */
export interface RegionOption {
  id: number;
  name: string;
  parent_id: number | null;
  flow: Flow;
  /** 0 for a flow's direct children, 1 for their children. */
  depth: number;
  /** Full name below the flow, e.g. "Cleanaway › Pilbara". */
  path: string;
}

export interface DifotReport {
  totals: DifotTotals;
  daily: DifotDaily[];
  by_region: DifotRegionRow[];
  collected: DifotCollected;
  drivers: DifotDriverRow[];
  options: { drivers: string[]; regions: RegionOption[] };
  /** Most recent trip_date with attempted deliveries — used to anchor relative ranges. */
  latest: string | null;
}

export interface DifotStop {
  stop_id: number;
  so: string | null;
  ref: string | null;
  status: string;
  trip_date: string;
  driver: string | null;
  team: string | null;
  state: string | null;
  region: string | null;
  item_quantity: number | null;
  enroute_time: string | null;
  arrived_time: string | null;
  completed_time: string | null;
  failed_time: string | null;
  distance_km: number | null;
  service_min: number | null;
  travel_min: number | null;
  w_start: string | null;
  w_end: string | null;
  /** Completed on the planned trip_date (the DIFOT "on time" leg). */
  on_time: boolean | null;
  /** Completed inside the SMS delivery window; null when the stop had no window. */
  in_window: boolean | null;
  in_full: boolean | null;
  fail_note: string | null;
  address: string | null;
  team_region_id: number | null;
  flow: Flow | null;
  region_parent: string | null;
}

export type DateRange = 'all' | 'today' | 'yesterday' | 'last7' | 'last30' | 'custom';
export type Period = 'daily' | 'weekly' | 'monthly';

/** Inclusive custom bounds (YYYY-MM-DD), only meaningful when range === 'custom'. */
export interface CustomRange {
  from?: string;
  to?: string;
}

export interface Filters extends CustomRange {
  range: DateRange;
  /** Unset reports across both flows (TV mode). */
  flow?: Flow;
  /** Selected team_region_ids as strings. Empty means every region in the flow. */
  regions: string[];
  /** Selected drivers / runs. Empty means every driver. */
  drivers: string[];
  status: string;
}

/**
 * Convert a UI date range into from/to bounds anchored to the live business day
 * (Australia/Sydney). "Today" means the real current calendar day — matched on
 * trip_date — not the most recent day that happens to have data.
 *
 * For `custom`, the caller's own bounds are used verbatim (swapped if entered
 * back-to-front); a missing bound stays null so the window is open-ended on
 * that side.
 */
export function rangeToDates(range: DateRange, custom?: CustomRange): { from: string | null; to: string | null } {
  if (range === 'custom') {
    const from = custom?.from?.trim() || null;
    const to = custom?.to?.trim() || null;
    return from && to && from > to ? { from: to, to: from } : { from, to };
  }
  if (range === 'all') return { from: null, to: null };
  if (range === 'yesterday') {
    const y = businessYesterdayISO();
    return { from: y, to: y };
  }
  const today = businessTodayISO();
  const t = new Date(`${today}T00:00:00Z`);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const minus = (d: Date, days: number) => {
    const x = new Date(d);
    x.setUTCDate(x.getUTCDate() - days);
    return x;
  };
  switch (range) {
    case 'today':
      return { from: today, to: today };
    case 'last7':
      return { from: iso(minus(t, 6)), to: today };
    case 'last30':
      return { from: iso(minus(t, 29)), to: today };
    default:
      return { from: null, to: null };
  }
}

/**
 * Starting bounds when the user switches the picker to "Custom" — seed it with
 * the window they were already looking at so the report doesn't jump, falling
 * back to the last 30 days when the previous window was open-ended ("all").
 */
export function defaultCustomRange(previous: DateRange, current: CustomRange): Required<CustomRange> {
  if (current.from && current.to) return { from: current.from, to: current.to };
  const seeded = rangeToDates(previous, current);
  const fallback = rangeToDates('last30');
  return {
    from: seeded.from ?? current.from ?? fallback.from ?? businessTodayISO(),
    to: seeded.to ?? current.to ?? businessTodayISO(),
  };
}

/**
 * Build a query string, dropping empty values. Array values are repeated
 * (`?driver=A&driver=B`) rather than joined, since driver names can themselves
 * contain a comma.
 */
function queryString(params: Record<string, string | string[] | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null) continue;
    if (Array.isArray(v)) {
      for (const item of v) if (item !== '') sp.append(k, item);
    } else if (v !== '') {
      sp.set(k, v);
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export function fetchReport(filters: Filters): Promise<DifotReport> {
  const { from, to } = rangeToDates(filters.range, filters);
  return apiFetch<DifotReport>(
    `/api/reports/difot${queryString({
      from,
      to,
      flow: filters.flow ?? null,
      region: filters.regions,
      driver: filters.drivers,
      status: filters.status,
    })}`,
  );
}

export function fetchStops(
  params: {
    from?: string | null;
    to?: string | null;
    flow?: Flow;
    regions?: string[];
    drivers?: string[];
    status?: string;
    q?: string;
    limit?: number;
  },
): Promise<DifotStop[]> {
  return apiFetch<DifotStop[]>(
    `/api/reports/difot/stops${queryString({
      from: params.from ?? null,
      to: params.to ?? null,
      flow: params.flow ?? null,
      region: params.regions ?? null,
      driver: params.drivers ?? null,
      status: params.status ?? null,
      q: params.q ?? null,
      limit: params.limit != null ? String(params.limit) : null,
    })}`,
  );
}

// ─── Failure categorisation (ported from the dashboard mockup) ──────────────

export interface FailCategory {
  key: string;
  label: string;
  color: string;
  patterns: RegExp[];
}

export const FAIL_CATEGORIES: FailCategory[] = [
  {
    key: 'customer_unavailable',
    label: 'Customer unavailable',
    color: '#8a7a6b',
    patterns: [/not home/i, /no answer/i, /no one home/i, /switched off/i, /didn.?t answer/i, /not avail/i, /couldn.?t get a hold/i, /no.?one was home/i, /not there/i, /wasn.?t at home/i, /no information provided/i],
  },
  {
    key: 'reschedule',
    label: 'Rescheduled / cancelled',
    color: '#f0a020',
    patterns: [/reschedule/i, /re ?schedule/i, /cancel/i, /didn.?t book/i, /not ready/i, /later date/i, /lunch time/i, /will be delivered at a later/i, /refer to store/i, /store called/i],
  },
  {
    key: 'damage',
    label: 'Damage / refused',
    color: '#d9412f',
    patterns: [/damage/i, /refused/i, /dust inside/i, /swap.?over/i, /not happy/i, /return/i, /broken/i],
  },
  {
    key: 'access',
    label: 'Access / loading',
    color: '#0a5c40',
    patterns: [/loading/i, /dock/i, /access/i, /can.?t get in/i, /no parking/i, /gate/i, /locked/i],
  },
  {
    key: 'address',
    label: 'Address / location',
    color: '#6abd5f',
    patterns: [/wrong address/i, /can.?t find/i, /address.*not/i, /no such/i, /incorrect address/i],
  },
  { key: 'other', label: 'Other / uncategorised', color: '#626866', patterns: [] },
];

export function categoriseFailure(note: string | null): string {
  if (!note || !note.trim()) return 'other';
  for (const c of FAIL_CATEGORIES) {
    if (c.key === 'other') continue;
    if (c.patterns.some((p) => p.test(note))) return c.key;
  }
  return 'other';
}

// ─── Period bucketing for the trend chart ──────────────────────────────────

export function periodKey(dateStr: string, period: Period): string {
  if (period === 'daily') return dateStr;
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (period === 'weekly') {
    const day = d.getUTCDay() || 7; // Mon=1..Sun=7
    d.setUTCDate(d.getUTCDate() - day + 1);
    return d.toISOString().slice(0, 10);
  }
  return `${dateStr.slice(0, 7)}-01`; // monthly
}

export function formatPeriodLabel(key: string, period: Period): string {
  const d = new Date(`${key}T00:00:00Z`);
  if (period === 'monthly') {
    return d.toLocaleDateString('en-AU', { month: 'short', year: '2-digit', timeZone: 'UTC' });
  }
  if (period === 'weekly') {
    return `Wk ${d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' })}`;
  }
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

export function formatDateAU(s: string | null): string {
  if (!s) return '—';
  return new Date(s.length <= 10 ? `${s}T00:00:00Z` : s).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Australia/Sydney',
  });
}


export function pct(n: number, d: number): number {
  return d ? (n / d) * 100 : 0;
}
